import { useCallback, useEffect, useMemo } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { UserResponse } from "stream-chat";
import {
  MessageList,
  MessageOptions,
  MessageSimple,
  renderText as defaultRenderText,
  TranslationProvider as StreamTranslationProvider,
  useChatContext,
  useChannelActionContext,
  useMessageComposer,
  useMessageContext,
  useTranslationContext,
} from "stream-chat-react";

import {
  CustomCallMessage,
  isCallLogMessage,
} from "@/features/chat/components/CustomCallMessage";
import {
  TranslatedBubble,
  useMessageTranslation,
} from "@/features/chat/components/MessageTranslation";
import { ReadReceipts } from "@/features/chat/components/ReadReceipts";
import {
  SUPPORTED_LANGUAGES,
  LANGUAGE_NATIVE_NAMES,
} from "@/features/chat/services/translationService";
import { useStartDirectChat } from "@/features/chat/hooks/useStartDirectChat";
import { useAppLinkLabels, withAppLinkLabels } from "@/shared/hooks/useAppLinkLabels";
import { useAppLinkClickHandler } from "@/shared/hooks/useOpenAppLink";

type RenderTextOptions = NonNullable<Parameters<typeof defaultRenderText>[2]>;

// Stream's default link renderer, plus: our own links open inside the app.
const MessageAnchor = ({ children, href }: { children?: ReactNode; href?: string }) => {
  const handleClick = useAppLinkClickHandler();
  const isEmail = href?.startsWith("mailto:");
  const isUrl = href?.startsWith("http");
  if (!href || (!isEmail && !isUrl)) return <>{children}</>;
  return (
    <a
      className={isUrl ? "str-chat__message-url-link" : undefined}
      href={href}
      rel="nofollow noreferrer noopener"
      target="_blank"
      onClick={(e) => handleClick(e, href)}
    >
      {children}
    </a>
  );
};

// Message text with our own links shown by name ("📄 report.pdf") instead of the URL.
const AppLinkMessageText = ({
  text,
  mentionedUsers,
  options,
}: {
  text: string;
  mentionedUsers?: UserResponse[];
  options?: RenderTextOptions;
}) => {
  const labels = useAppLinkLabels(text);
  return defaultRenderText(withAppLinkLabels(text, labels), mentionedUsers, {
    ...options,
    customMarkDownRenderers: { ...options?.customMarkDownRenderers, a: MessageAnchor },
  });
};

const renderMessageText = (
  text?: string,
  mentionedUsers?: UserResponse[],
  options?: RenderTextOptions,
) =>
  text ? <AppLinkMessageText text={text} mentionedUsers={mentionedUsers} options={options} /> : null;

// Curved reply arrow icon used for the Reply button in the message toolbar
const ReplyIcon = ({ className = "" }: { className?: string }) => (
  <svg
    className={className}
    height="14"
    width="14"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
  >
    <path
      d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z"
      fillRule="evenodd"
    />
  </svg>
);

// Wraps MessageOptions so the toolbar button triggers inline quote-reply (not thread)
export const CustomMessageOptions = () => {
  const translationCtx = useTranslationContext("CustomMessageOptions");
  const { message } = useMessageContext("CustomMessageOptions");
  const messageComposer = useMessageComposer();

  // Inline quote-reply: set quoted message and focus the input
  const handleReply = useCallback(() => {
    messageComposer.setQuotedMessage(message);
    const selector = message.parent_id
      ? ".str-chat__thread .str-chat__textarea__textarea"
      : ".str-chat__textarea__textarea";
    const textarea = document.querySelector(selector);
    if (textarea instanceof HTMLTextAreaElement) textarea.focus();
  }, [messageComposer, message]);

  const originalT = translationCtx.t;
  const customT = ((key: string, ...args: any[]) => {
    if (key === "aria/Open Thread") return "Reply";
    return (originalT as any)(key, ...args);
  }) as unknown as typeof originalT;

  return (
    <StreamTranslationProvider value={{ ...translationCtx, t: customT }}>
      <MessageOptions ThreadIcon={ReplyIcon} handleOpenThread={handleReply} />
    </StreamTranslationProvider>
  );
};

// System event message — member added / removed / left group
const SystemEventMessage = () => {
  const { message } = useMessageContext();
  const { t } = useTranslation();
  const event = (message as any).hp_system_event as string | undefined;
  const actor = (message as any).hp_actor_name as string | undefined;
  const member = (message as any).hp_member_name as string | undefined;
  let text = "";
  if (event === "member_added") text = t("chat.system_member_added", { actor, member });
  else if (event === "member_removed") text = t("chat.system_member_removed", { actor, member });
  else if (event === "member_left") text = t("chat.system_member_left", { member });
  else text = (message as any).text || "";
  return (
    <div className="flex justify-center my-1 px-4">
      <span className="text-xs text-slate-400 bg-slate-100 rounded-full px-3 py-1 text-center">
        {text}
      </span>
    </div>
  );
};

// Custom message component — intercepts call_log messages and adds translation + read receipts
export const CustomMessage = () => {
  const { message } = useMessageContext();
  const { channel, client } = useChatContext();
  const { i18n } = useTranslation();
  const { autoTranslateEnabled, addTranslationFromStreamCache, translateMessage } = useMessageTranslation();

  // Auto-translate incoming messages when auto_translate_chat is on.
  // Priority: read from Stream message custom fields (set by webhook, free) →
  // fallback: call translateMessage() which uses the 3-tier cache (Stream → gtx).
  useEffect(() => {
    if (!autoTranslateEnabled) return;
    if (!message?.id) return;
    if (message.user?.id === client.userID) return; // own messages

    // Skip deleted messages
    if ((message as any).deleted_at || message.type === "deleted") return;

    // Skip if no text (attachment-only or image-only message)
    const rawText = message.text?.trim() ?? "";
    if (!rawText) return;

    // Skip if text is purely a URL (link preview messages)
    if (/^https?:\/\/\S+$/.test(rawText)) return;

    const targetLang = (i18n.language?.split("-")[0] || "en") as string;
    const cacheKey = `translations_${targetLang}`;
    const storedTranslation = (message as any)[cacheKey];
    const storedOriginal = (message as any)[`${cacheKey}_original`];

    if (storedTranslation && storedOriginal === message.text) {
      // Webhook already stored it — show for free
      addTranslationFromStreamCache(message.id, {
        text: storedTranslation,
        targetLang,
        sourceLang: (message as any)[`${cacheKey}_source`],
        sourceText: message.text!,
      });
    } else {
      // Fallback: translateMessage checks Stream cache first (tier 2), then gtx (tier 3)
      translateMessage(message.id, message.text!);
    }
  }, [message, autoTranslateEnabled, client.userID, i18n.language, addTranslationFromStreamCache, translateMessage]);

  if ((message as any).hp_system_event) {
    return <SystemEventMessage />;
  }

  if (isCallLogMessage(message)) {
    return <CustomCallMessage message={message} />;
  }

  // Stream auto-generates a "Missed call from X (call creator)" message when a
  // ringed call ends. Hide it in group channels — our sendCallLog already sends
  // the correct "Call ended · Duration" pill. Leave 1:1 channels untouched.
  const isGroupChannel = Object.keys(channel?.state.members || {}).length > 2;
  if (isGroupChannel && message.text?.includes("(call creator)")) {
    return null;
  }

  return (
    <div>
      <MessageSimple />
      {message?.id && message?.text && (
        <TranslatedBubble messageId={message.id} currentText={message.text} />
      )}
      {message?.id && (
        <ReadReceipts
          messageId={message.id}
          messageCreatedAt={message.created_at}
        />
      )}
    </div>
  );
};

// MessageList wrapper that injects the "Translate" custom action + built-in pin
export const TranslatedMessageList = ({
  onCreateTask,
  jumpToMessageId,
  onJumpComplete,
}: {
  onCreateTask?: (message: any, channelMembers: any[]) => void;
  jumpToMessageId?: string;
  onJumpComplete?: (messageId: string) => void;
}) => {
  const { t, i18n } = useTranslation();
  const { translateMessage } = useMessageTranslation();
  const { jumpToMessage, openThread } = useChannelActionContext("TranslatedMessageList");
  const { channel } = useChatContext();
  const startDirectChat = useStartDirectChat();

  const targetLangCode = i18n.language?.split("-")[0] || "en";
  const targetLangInfo = SUPPORTED_LANGUAGES.find(
    (l) => l.code === targetLangCode,
  );
  const translateLabel = t("chat.translate", {
    language: LANGUAGE_NATIVE_NAMES[targetLangCode] || targetLangInfo?.name || "English",
  });
  const threadLabel = t("chat.thread", { defaultValue: "Thread" });
  const createTaskLabel = t("tasks.chat_action_create_task", { defaultValue: "Create Task" });

  useEffect(() => {
    if (!jumpToMessageId) return;

    let cancelled = false;
    const jump = async () => {
      try {
        await jumpToMessage(jumpToMessageId, undefined, 4000);
      } catch (error) {
        console.warn("[Chat] Failed to jump to mentioned message:", error);
      } finally {
        if (!cancelled) onJumpComplete?.(jumpToMessageId);
      }
    };

    void jump();
    return () => {
      cancelled = true;
    };
  }, [jumpToMessage, jumpToMessageId, onJumpComplete]);

  const customMessageActions = useMemo(
    () => ({
      [translateLabel]: (message: any) => {
        if (message?.id && message?.text) {
          translateMessage(message.id, message.text);
        }
      },
      [threadLabel]: (message: any, event: any) => openThread(message, event),
      ...(onCreateTask
        ? {
            [createTaskLabel]: (message: any) => {
              const members = Object.values(channel?.state.members ?? {}).map(
                (m: any) => ({
                  user_id: m.user_id,
                  first_name: m.user?.name?.split(" ")[0] ?? "",
                  last_name: m.user?.name?.split(" ").slice(1).join(" ") ?? "",
                  profile_pic_id: (m.user as any)?.profile_pic_id,
                }),
              );
              onCreateTask(message, members);
            },
          }
        : {}),
    }),
    [translateLabel, threadLabel, translateMessage, openThread, onCreateTask, createTaskLabel, channel],
  );

  // "pin" is a built-in Stream action that uses usePinHandler (optimistic update + capability check)
  return (
    <MessageList
      customMessageActions={customMessageActions}
      messageActions={[
        "react",
        "reply",
        "edit",
        "delete",
        "flag",
        "mute",
        "markUnread",
        "pin",
      ]}
      renderText={renderMessageText}
      onMentionsClick={(_event, users) => {
        if (users[0]) startDirectChat(users[0].id);
      }}
    />
  );
};
